import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Box, Button, Card, CardContent, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography, useMediaQuery, useTheme } from '@mui/material';
import { Add } from '@mui/icons-material';
import api from '../services/api';
import { RootState } from '../store';
import { User } from '../types';

export const SellerActivitiesPage: React.FC = () => {
  const { user } = useSelector((s: RootState) => s.auth);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isAdmin = useMemo(() => {
    const role = (user?.role || '').toLowerCase();
    return role === 'admin' || role === 'manager';
  }, [user?.role]);
  const isSeller = (user?.role || '').toLowerCase() === 'seller';
  const isCompactView = isSeller || isMobile;
  const [items, setItems] = useState<any[]>([]);
  const [seller, setSeller] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<any>({ sellerName: '', channel: 'Call', activityAt: '', result: '', nextStep: '', nextStepAt: '', notes: '' });
  const [report, setReport] = useState<{ from?: string; to?: string; items: any[] }>({ items: [] });
  const [pending, setPending] = useState<any[]>([]);
  const [sellers, setSellers] = useState<User[]>([]);
  const [loadingSellers, setLoadingSellers] = useState(false);

  useEffect(() => {
    if (isSeller && user?.name) {
      setSeller(user.name);
      setForm((prev: any) => ({ ...prev, sellerName: user.name }));
    }
  }, [isSeller, user?.name]);

  const load = async () => {
    const res = await api.listSellerActivities({ seller: seller || undefined, from: from || undefined, to: to || undefined });
    setItems(res.items || []);
    const r = await api.getSellerReport({ seller: seller || undefined, from: from || undefined, to: to || undefined });
    setReport(r);
    const pn = await api.getPendingNextSteps({});
    setPending(pn.items || []);
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    const loadSellers = async () => {
      if (!isAdmin) return;
      setLoadingSellers(true);
      try {
        const res = await api.getUsers({ role: 'Seller' });
        setSellers(res.items || []);
        if (!form.sellerName && res.items?.length) {
          setForm((prev: any) => ({ ...prev, sellerName: res.items[0].name }));
        }
      } finally {
        setLoadingSellers(false);
      }
    };
    loadSellers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const save = async () => {
    const payload = { ...form };
    if (!payload.sellerName) return;
    await api.createSellerActivity(payload);
    setOpen(false);
    await load();
  };

  const openDialog = () => {
    setForm((prev: any) => {
      if (isSeller && user?.name) return { ...prev, sellerName: user.name };
      if (!prev.sellerName && sellers.length > 0) return { ...prev, sellerName: sellers[0].name };
      return prev;
    });
    setOpen(true);
  };

  const ActivityCard: React.FC<{ activity: any }> = ({ activity }) => (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardContent>
        <Typography variant="subtitle1" fontWeight={600}>{activity.customerName || 'Cliente sin nombre'}</Typography>
        <Typography variant="body2" color="text.secondary">{activity.sellerName} • {activity.channel}</Typography>
        <Typography variant="body2" sx={{ mt: 0.5 }}>{new Date(activity.activityAt).toLocaleString()}</Typography>
        {activity.result && <Typography variant="body2">Resultado: {activity.result}</Typography>}
        {activity.nextStep && (
          <Typography variant="body2" color="text.secondary">
            Próximo paso: {activity.nextStep} {activity.nextStepAt && `(${new Date(activity.nextStepAt).toLocaleDateString()})`}
          </Typography>
        )}
        {activity.notes && <Typography variant="body2" color="text.secondary">Notas: {activity.notes}</Typography>}
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ md: 'center' }} sx={{ mb: 2 }}>
        <Typography variant="h4" fontWeight={700}>Actividad de vendedores</Typography>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} sx={{ width: '100%', maxWidth: 760 }}>
          {!isSeller && (
            <TextField select size="small" label="Vendedor" value={seller} onChange={e => setSeller(e.target.value)} sx={{ minWidth: 200 }} disabled={loadingSellers}>
              <MenuItem value="">Todos</MenuItem>
              {sellers.map((s) => (
                <MenuItem key={s.id} value={s.name}>{s.name}</MenuItem>
              ))}
            </TextField>
          )}
          <TextField size="small" type="date" label="Desde" InputLabelProps={{ shrink: true }} value={from} onChange={e => setFrom(e.target.value)} />
          <TextField size="small" type="date" label="Hasta" InputLabelProps={{ shrink: true }} value={to} onChange={e => setTo(e.target.value)} />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <Button variant="outlined" onClick={load}>Filtrar</Button>
            <Button startIcon={<Add />} variant="contained" onClick={openDialog}>Registrar actividad</Button>
          </Stack>
        </Stack>
      </Stack>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Reporte por vendedor</Typography>
            {report.items?.length === 0 && <Typography variant="body2" color="text.secondary">Sin datos</Typography>}
            {report.items?.length > 0 && (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Vendedor</TableCell>
                      <TableCell align="right">Contactos</TableCell>
                      <TableCell align="right">Opps creadas</TableCell>
                      <TableCell align="right">Opps ganadas</TableCell>
                      <TableCell align="right">Valor ganado</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {report.items.map((r: any) => (
                      <TableRow key={r.seller}>
                        <TableCell>{r.seller}</TableCell>
                        <TableCell align="right">{r.contacts}</TableCell>
                        <TableCell align="right">{r.opportunitiesCreated}</TableCell>
                        <TableCell align="right">{r.opportunitiesWon}</TableCell>
                        <TableCell align="right">$ {r.wonValue?.toLocaleString()}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Próximos pasos (7 días)</Typography>
            {pending.length === 0 && <Typography variant="body2" color="text.secondary">Sin pendientes</Typography>}
            {pending.map(p => (
              <Stack direction="row" key={p.id} justifyContent="space-between" sx={{ py: 0.5 }}>
                <Typography>{p.customer || '—'}</Typography>
                <Typography color="text.secondary">{p.nextStep} · {new Date(p.nextStepAt).toLocaleDateString()}</Typography>
              </Stack>
            ))}
          </CardContent>
        </Card>
      </Stack>

      {isCompactView ? (
        <Box sx={{ mt: 1 }}>
          {items.map((a) => <ActivityCard key={a.id} activity={a} />)}
          {items.length === 0 && <Typography variant="body2" color="text.secondary">Sin actividades registradas.</Typography>}
        </Box>
      ) : (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Actividades</Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Fecha</TableCell>
                    <TableCell>Vendedor</TableCell>
                    <TableCell>Cliente</TableCell>
                    <TableCell>Canal</TableCell>
                    <TableCell>Resultado</TableCell>
                    <TableCell>Próximo paso</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((a) => (
                    <TableRow key={a.id}>
                      <TableCell>{new Date(a.activityAt).toLocaleString()}</TableCell>
                      <TableCell>{a.sellerName}</TableCell>
                      <TableCell>{a.customerName || '—'}</TableCell>
                      <TableCell>{a.channel}</TableCell>
                      <TableCell>{a.result}</TableCell>
                      <TableCell>{a.nextStep} {a.nextStepAt && `(${new Date(a.nextStepAt).toLocaleDateString()})`}</TableCell>
                    </TableRow>
                  ))}
                  {items.length === 0 && (<TableRow><TableCell colSpan={6}><Typography variant="body2" color="text.secondary">Sin actividades</Typography></TableCell></TableRow>)}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Registrar actividad</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              select
              label="Vendedor"
              value={form.sellerName}
              onChange={e => setForm({ ...form, sellerName: e.target.value })}
              required
              disabled={isSeller || loadingSellers}
              helperText={isSeller ? 'Se completa con tu usuario' : undefined}
            >
              {(isSeller && user?.name) && <MenuItem value={user.name}>{user.name}</MenuItem>}
              {!isSeller && <MenuItem value="">Seleccionar</MenuItem>}
              {!isSeller && sellers.map(s => (
                <MenuItem key={s.id} value={s.name}>{s.name}</MenuItem>
              ))}
            </TextField>
            <TextField select label="Canal" value={form.channel} onChange={e => setForm({ ...form, channel: e.target.value })}>
              <MenuItem value="Call">Llamado</MenuItem>
              <MenuItem value="Visit">Visita</MenuItem>
              <MenuItem value="WhatsApp">WhatsApp</MenuItem>
              <MenuItem value="Email">Email</MenuItem>
            </TextField>
            <TextField label="Fecha y hora" type="datetime-local" InputLabelProps={{ shrink: true }} value={form.activityAt} onChange={e => setForm({ ...form, activityAt: e.target.value })} />
            <TextField label="Resultado" value={form.result} onChange={e => setForm({ ...form, result: e.target.value })} />
            <TextField label="Próximo paso" value={form.nextStep} onChange={e => setForm({ ...form, nextStep: e.target.value })} />
            <TextField label="Fecha próximo paso" type="date" InputLabelProps={{ shrink: true }} value={form.nextStepAt} onChange={e => setForm({ ...form, nextStepAt: e.target.value })} />
            <TextField label="Notas" multiline minRows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={save}>Guardar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
